import React from 'react';
import { Row, Col, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { camelCase } from 'lodash';

import { HealthClaim } from '@models/HealthClaim';
import { PaymentMethods } from '@models/PaymentInfo';
import { formatDateTime } from '@utils/datetime';
import { RowItem } from './row-item';

interface ClaimDetailsTabContentProps {
  healthClaim: HealthClaim;
}

export const ClaimDetailsTabContent = ({ healthClaim }: ClaimDetailsTabContentProps) => {
  const { t } = useTranslation();
  const isPaymentByBankTransfer = healthClaim.paymentInfo?.paymentMethod === PaymentMethods.BankTransfer;
  const diagnosisCodes = healthClaim.diagnosisCodes || [];

  return (
    <>
      <h6 className="mb-3">{t('healthClaimDetails.eventDetails')}</h6>
      <Row>
        <Col md={6} className="mb-3">
          <RowItem
            label={t('healthClaimDetails.dateOfAdmission')}
            value={formatDateTime(healthClaim.accidentHappenedAt)}
          />
        </Col>
        <Col md={6} className="mb-3">
          <RowItem label={t('healthClaimDetails.dischargeDate')} value={formatDateTime(healthClaim.dischargedAt)} />
        </Col>
        <Col md={6} className="mb-3">
          <RowItem label={t('healthClaimDetails.hospitalLengthOfStay')} value={healthClaim.hospitalLengthOfStay} />
        </Col>
        <Col md={6} className="mb-3">
          <RowItem label={t('healthClaimDetails.symptoms')} value={healthClaim.symptomsDescription} />
        </Col>
        <Col md={6} className="mb-3">
          <RowItem label={t('healthClaimDetails.diagnosis')} value={healthClaim.diagnosisDescription} />
        </Col>
        <Col md={6} className="mb-3">
          <RowItem label={t('healthClaimDetails.panelHospitalName')} value={healthClaim.businessPartner?.name} />
        </Col>
        <Col md={6} className="mb-3">
          <RowItem label={t('healthClaimDetails.hospitalName')} value={healthClaim.hospitalName} />
        </Col>
      </Row>

      <h6 className="mb-3 mt-4">{t('healthClaimDetails.diagnosisDetails')}</h6>
      {diagnosisCodes.length > 0 ? (
        <Table size="sm" bordered>
          <thead>
            <tr>
              <th width="25%">{t('healthClaimDetails.diagnosisCodes')}</th>
              <th width="75%">{t('healthClaimDetails.diagnosis')}</th>
            </tr>
          </thead>
          <tbody>
            {diagnosisCodes.map((dc) => (
              <tr key={dc.id}>
                <td>{dc.code}</td>
                <td>{dc.description}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted">&mdash;</p>
      )}

      <h6 className="mb-3 mt-4">{t('healthClaimDetails.paymentInformation')}</h6>
      <Row>
        <Col md={6} className="mb-3">
          <RowItem
            label={t('paymentInfo.paymentMethod')}
            value={t(`paymentInfo.${camelCase(healthClaim.paymentInfo?.paymentMethod)}`)}
          />
        </Col>
        <Col md={6} className="mb-3">
          <RowItem label={t('paymentInfo.beneficiaryName')} value={healthClaim.paymentInfo?.beneficiaryName} />
        </Col>
        {isPaymentByBankTransfer && (
          <>
            <Col md={6} className="mb-3">
              <RowItem label={t('paymentInfo.bankName')} value={healthClaim.paymentInfo?.bankName} />
            </Col>
            <Col md={6} className="mb-3">
              <RowItem label={t('paymentInfo.bankAccount')} value={healthClaim.paymentInfo?.bankAccount} />
            </Col>
          </>
        )}
      </Row>
    </>
  );
};
